import { PaymentOption } from "src/enums/payment-option";

/**
 * Where "continue my unfinished order" should land.
 *
 * NEVER SKIP EMAIL CAPTURE
 *
 * Resuming used to jump straight to delivery (or payment-on-delivery), stepping
 * over photo-options -- which is where the email is taken. That step exists to
 * capture it EARLY, before the customer invests in the address form, so a cart
 * abandoned at delivery can still be recovered. Skipping it means someone who
 * drops out at delivery leaves no address to write to at all.
 *
 * Measured: 491 of 877 abandoned carts holding photos in a 14-day window had no
 * email, so they were unreachable. The funnel puts email capture (55.9%) ahead
 * of address entry (47.1%) -- the gap between those steps is exactly the
 * population this protects.
 *
 * A returning customer who already gave an email carries straight on, with no
 * extra step for someone we can already reach.
 *
 * Extracted from the two components that resume an order -- the unfinished-order
 * modal and the resume-old-order button -- which each carried their own copy of
 * this rule. One of them can be fixed and the other missed, and neither was
 * testable in place.
 */
export type ResumeStep = "photo-options" | "payment-on-delivery" | "delivery";

export interface ResumableOrder {
  shippingEmail?: string | null;
  /**
   * Compared by value, not by enum identity: the generated API client types
   * this as its own string union, and the two components pass different shapes.
   */
  paymentOption?: string | null;
}

export function resumeStepFor(order: ResumableOrder): ResumeStep {
  // Treat whitespace as absent: a blank string is not an address we can reach.
  if (!order?.shippingEmail || order.shippingEmail.trim() === "") {
    return "photo-options";
  }
  if (order.paymentOption === PaymentOption.PAYMENT_ON_DELIVERY) {
    return "payment-on-delivery";
  }
  return "delivery";
}

/** The path to push for a resumed order, carrying the guest token. */
export function resumeUrlFor(
  origin: string,
  orderId: string | undefined,
  order: ResumableOrder,
  guestToken: string | null | undefined,
): string {
  const url = new URL(`${origin}/order/${orderId}/${resumeStepFor(order)}`);
  url.searchParams.set("guest_session_token", guestToken || "");
  return url.pathname + url.search;
}
